/**
 * Temporary Q3-2026 VAT diagnostic — REMOVABLE.
 *
 * Compares production's live-balance VAT numbers against the period-scoped
 * (2026-07-01 → 2026-09-30) sum of e-conomic postings on the same
 * accounts, and verifies each configured account's actual name in
 * e-conomic. Read-only. Does NOT modify the production calculation.
 *
 * Delete this file, the route in App.js, and the backend route
 * `routes/vat_diagnostic.py` once the investigation is closed.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Header } from '../../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { API_BASE } from '../../lib/apiBase';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (n) => (n === null || n === undefined || Number.isNaN(n)
    ? '—'
    : new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n));

function StatusBadge({ status }) {
    if (status === 'ok') {
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> ok
        </Badge>;
    }
    if (status === 'not_configured') {
        return <Badge className="bg-slate-100 text-slate-600 border-slate-200">not configured</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700 border-red-200">
        <XCircle className="h-3.5 w-3.5 mr-1" /> {status}
    </Badge>;
}

export default function VatDiagnosticPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${API_BASE}/api/finance/diagnostics/vat-q3-2026`,
                { headers: authHeaders() },
            );
            setData(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to load diagnostic');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="min-h-screen bg-background" data-testid="vat-diagnostic-page">
            <Header />
            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900">
                            VAT Q3-2026 diagnostic <span className="text-sm text-slate-400 font-normal">(temporary, read-only)</span>
                        </h1>
                        <p className="text-sm text-slate-500">
                            Compares the production "Next VAT Settlement" figure (live account balances)
                            against the correct period-scoped Q3-2026 sum of e-conomic postings.
                            Nothing is written. Delete this page once the investigation is closed.
                        </p>
                    </div>
                    <Button onClick={load} disabled={loading} variant="outline"
                            data-testid="vat-diag-refresh-btn">
                        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Refresh
                    </Button>
                </div>

                {!data && !loading && (
                    <div className="text-sm text-slate-500">No data.</div>
                )}

                {data && !data.econonomic_configured && (
                    <Card><CardContent className="py-6 text-sm text-slate-500">
                        e-conomic is not configured in this environment.
                    </CardContent></Card>
                )}

                {data?.econonomic_configured && (
                    <>
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Period + configuration</CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <div className="text-xs text-slate-500">Period</div>
                                    <div className="font-medium">{data.period.start} → {data.period.end}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500">Filing frequency</div>
                                    <div className="font-medium">{data.configured_accounts.frequency}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500">Sales VAT account</div>
                                    <div className="font-medium">{data.configured_accounts.output_account ?? '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500">Purchase VAT account</div>
                                    <div className="font-medium">{data.configured_accounts.input_account ?? '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500">Import VAT account</div>
                                    <div className="font-medium">{data.configured_accounts.import_account ?? '—'}</div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Per-account breakdown</CardTitle>
                                <CardDescription>
                                    Verifies each configured account exists in e-conomic under the
                                    expected name, then compares the CURRENT BALANCE (what production uses)
                                    against the Q3-only POSTINGS SUM (what production should use).
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="vat-diag-accounts-table">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Role</th>
                                            <th className="text-left px-3 py-2 font-medium">Acct #</th>
                                            <th className="text-left px-3 py-2 font-medium">Name (e-conomic)</th>
                                            <th className="text-right px-3 py-2 font-medium">Current balance</th>
                                            <th className="text-right px-3 py-2 font-medium">Q3 sum (signed)</th>
                                            <th className="text-right px-3 py-2 font-medium">Q3 sum (abs)</th>
                                            <th className="text-right px-3 py-2 font-medium">Q3 entries</th>
                                            <th className="text-left px-3 py-2 font-medium">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.accounts.map((a) => (
                                            <tr key={a.role} className="border-t hover:bg-slate-50"
                                                data-testid={`vat-diag-row-${a.role}`}>
                                                <td className="px-3 py-2">
                                                    <div className="font-medium">{a.label}</div>
                                                    <div className="text-xs text-slate-500">{a.role}</div>
                                                </td>
                                                <td className="px-3 py-2 font-mono">{a.configured_account_number ?? '—'}</td>
                                                <td className="px-3 py-2">{a.account_name || <span className="text-slate-400">—</span>}</td>
                                                <td className="px-3 py-2 text-right font-mono">{fmt(a.current_balance_native)}</td>
                                                <td className="px-3 py-2 text-right font-mono">{fmt(a.q3.sum_signed_native)}</td>
                                                <td className="px-3 py-2 text-right font-mono">{fmt(a.q3.sum_abs_native)}</td>
                                                <td className="px-3 py-2 text-right font-mono">{a.q3.entry_count}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-col gap-1">
                                                        <StatusBadge status={a.status} />
                                                        {a.q3.status !== 'ok' && a.q3.status !== 'not_configured' && (
                                                            <span className="text-xs text-red-600">Q3: {a.q3.status}</span>
                                                        )}
                                                        {a.error && <span className="text-xs text-red-600">{a.error}</span>}
                                                        {a.q3.error && <span className="text-xs text-red-600">{a.q3.error}</span>}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>

                        <div className="grid gap-4 md:grid-cols-2">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        Production (current balances)
                                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                            <AlertTriangle className="h-3.5 w-3.5 mr-1" /> live
                                        </Badge>
                                    </CardTitle>
                                    <CardDescription>What the Cashflow page shows today.</CardDescription>
                                </CardHeader>
                                <CardContent className="text-sm">
                                    <SnapshotRows d={data.production_snapshot_using_current_balances} />
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        Q3-only period sums
                                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">correct</Badge>
                                    </CardTitle>
                                    <CardDescription>Sum of postings dated {data.period.start} → {data.period.end}.</CardDescription>
                                </CardHeader>
                                <CardContent className="text-sm">
                                    <SnapshotRows d={data.calculated_q3_using_period_sums} />
                                </CardContent>
                            </Card>
                        </div>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Sample Q3 entries (first ~8 per account)</CardTitle>
                            </CardHeader>
                            <CardContent className="overflow-x-auto">
                                {data.accounts.map((a) => (
                                    <div key={a.role} className="mb-4">
                                        <div className="text-sm font-medium mb-1">
                                            {a.label} · {a.configured_account_number} · {a.account_name || '—'}
                                        </div>
                                        {(a.q3.sample_entries?.length || 0) === 0 ? (
                                            <div className="text-xs text-slate-400 pl-2">No Q3 entries found.</div>
                                        ) : (
                                            <table className="w-full text-xs border">
                                                <thead className="bg-slate-50">
                                                    <tr>
                                                        <th className="text-left px-2 py-1 font-medium">Year</th>
                                                        <th className="text-left px-2 py-1 font-medium">Date</th>
                                                        <th className="text-left px-2 py-1 font-medium">Voucher</th>
                                                        <th className="text-left px-2 py-1 font-medium">Type</th>
                                                        <th className="text-right px-2 py-1 font-medium">Amount</th>
                                                        <th className="text-left px-2 py-1 font-medium">Text</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {a.q3.sample_entries.map((e, i) => (
                                                        <tr key={i} className="border-t">
                                                            <td className="px-2 py-1">{e.year}</td>
                                                            <td className="px-2 py-1">{(e.date || '').slice(0, 10)}</td>
                                                            <td className="px-2 py-1 font-mono">{e.voucher}</td>
                                                            <td className="px-2 py-1">{e.entry_type}</td>
                                                            <td className="px-2 py-1 text-right font-mono">{fmt(e.amount)}</td>
                                                            <td className="px-2 py-1 text-slate-500">{e.text}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        <p className="text-xs text-slate-400">
                            {data.notes}
                        </p>
                    </>
                )}
            </main>
        </div>
    );
}

function SnapshotRows({ d }) {
    if (!d) return <div className="text-slate-400">—</div>;
    return (
        <table className="w-full text-sm">
            <tbody>
                <Row label="Sales VAT" value={d.sales_vat_native} />
                <Row label="Purchase VAT" value={d.purchase_vat_native} />
                <Row label="Import VAT" value={d.import_vat_native} />
                <tr className="border-t"><td className="py-2 font-semibold">Net payable</td>
                    <td className="py-2 text-right font-mono font-semibold">
                        {fmt(d.net_payable_native)} {d.currency || 'DKK'}
                    </td></tr>
                {d.source && <tr><td colSpan={2} className="text-xs text-slate-400 pt-1">source: {d.source}</td></tr>}
            </tbody>
        </table>
    );
}

function Row({ label, value }) {
    return (
        <tr>
            <td className="py-1 text-slate-600">{label}</td>
            <td className="py-1 text-right font-mono">{fmt(value)}</td>
        </tr>
    );
}
